import React from 'react';
import { Text, Box, Paper, Skeleton, Select, Progress } from "@mantine/core";
import { Badge, DataTable } from "@xplatform/design-system";
import type { CostBreakdownViewModel, CostDimension } from '@models/cost';
import { formatCurrency } from '@utils/formatters';
import './CostBreakdownTable.scss';

type BreakdownRow = CostBreakdownViewModel['breakdown'][number];

interface CostBreakdownTableProps {
    data: CostBreakdownViewModel | null;
    isLoading: boolean;
    selectedDimension: CostDimension;
    onDimensionChange: (dimension: CostDimension) => void;
}

const CostBreakdownTable: React.FC<CostBreakdownTableProps> = ({
    data,
    isLoading,
    selectedDimension,
    onDimensionChange,
}) => {
    const dimensions: Array<{ value: CostDimension; label: string }> = [
        { value: 'module', label: 'Module' },
        { value: 'service', label: 'Service' },
        { value: 'user', label: 'User' },
        { value: 'organisation', label: 'Organisation' },
        { value: 'provider', label: 'Provider' },
        { value: 'capability', label: 'Capability' },
    ];

    const getPercentageColor = (percentage: number) => {
        if (percentage >= 50) return 'red';
        if (percentage >= 25) return 'yellow';
        return 'green';
    };

    const columns = [
        {
            key: 'name',
            header: 'Name',
            width: '12%',
            render: (item: BreakdownRow) => (
                <Text size="sm" fw={500}>
                    {item.dimensionValue}
                </Text>
            ),
        },
        {
            key: 'totalCost',
            header: 'Total Cost',
            width: '10%',
            align: 'right' as const,
            render: (item: BreakdownRow) => (
                <Text size="sm" fw={600}>
                    {formatCurrency(item.totalCost, item.currency)}
                </Text>
            ),
        },
        {
            key: 'events',
            header: 'Events',
            width: '10%',
            align: 'right' as const,
            render: (item: BreakdownRow) => (
                <Badge variant="outline" color="gray" size="sm">
                    {item.eventCount.toLocaleString()}
                </Badge>
            ),
        },
        {
            key: 'users',
            header: 'Users',
            width: '10%',
            align: 'right' as const,
            render: (item: BreakdownRow) => (
                <Badge variant="outline" color="green" size="sm">
                    {item.usersCount.toLocaleString()}
                </Badge>
            ),
        },
        {
            key: 'percentage',
            header: 'Percentage',
            width: '10%',
            align: 'center' as const,
            render: (item: BreakdownRow) => (
                <Badge color={getPercentageColor(item.percentage)} size="sm">
                    {`${item.percentage.toFixed(1)}%`}
                </Badge>
            ),
        },
        {
            key: 'distribution',
            header: 'Distribution',
            width: '25%',
            align: 'right' as const,
            render: (item: BreakdownRow) => (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Box style={{ flex: 1 }}>
                        <Progress
                            value={item.percentage}
                            color={getPercentageColor(item.percentage)}
                            size="sm"
                            radius="xl"
                        />
                    </Box>
                </Box>
            ),
        },
    ];

    return (
        <Box className="cost-breakdown-table-card">
            <Box className="table-header">
                <Box>
                    <Text fw={600} size="lg" mb={4}>
                        Cost Breakdown
                    </Text>
                    <Text size="sm" c="dimmed">
                        Analyze costs by different dimensions
                    </Text>
                </Box>
                <Select
                    label="Dimension"
                    value={selectedDimension}
                    onChange={(value) => value && onDimensionChange(value as CostDimension)}
                    data={dimensions}
                    size="sm"
                    w={200}
                    comboboxProps={{ withinPortal: false }}
                />
            </Box>

            <Paper className="table-container">
                {isLoading ? (
                    <Skeleton height={300} mt="sm" />
                ) : !data || data.breakdown.length === 0 ? (
                    <Box
                        style={{
                            height: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Text c="dimmed">No breakdown data available</Text>
                    </Box>
                ) : (
                    <DataTable<BreakdownRow>
                        columns={columns}
                        data={data.breakdown}
                        getRowKey={(_row, index) => index}
                        footer={[
                            {
                                content: (
                                    <Text size="sm" fw={700}>
                                        Total
                                    </Text>
                                ),
                            },
                            {
                                content: (
                                    <Text size="sm" fw={700}>
                                        {formatCurrency(data.totalCost, data.currency)}
                                    </Text>
                                ),
                                align: 'right',
                            },
                            { content: null, colSpan: 4 },
                        ]}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default CostBreakdownTable;
